"use strict";
// toMoney is the single rounding boundary. See README.money-rule.md beside this file.
Object.defineProperty(exports, "__esModule", { value: true });
exports.MoneyAllocationError = exports.ZERO_MONEY = exports.MoneyPrecisionError = exports.MoneyFormatError = exports.MONEY_ROUNDING_RULE = exports.MONEY_PRECISION = exports.PRINT_SCALE = exports.MONEY_SCALE = void 0;
exports.toMoney = toMoney;
exports.toPrintMoney = toPrintMoney;
exports.isMoney = isMoney;
exports.addMoney = addMoney;
exports.subtractMoney = subtractMoney;
exports.sumMoney = sumMoney;
exports.negateMoney = negateMoney;
exports.absMoney = absMoney;
exports.compareMoney = compareMoney;
exports.isZeroMoney = isZeroMoney;
exports.isNegativeMoney = isNegativeMoney;
exports.allocateEqually = allocateEqually;
exports.allocateByPercentage = allocateByPercentage;
exports.allocateByWeight = allocateByWeight;
exports.MONEY_SCALE = 4;
exports.PRINT_SCALE = 2;
exports.MONEY_PRECISION = 18;
exports.MONEY_ROUNDING_RULE = 'half-away-from-zero';
const MAX_INTEGER_DIGITS = exports.MONEY_PRECISION - exports.MONEY_SCALE;
const SCALE_FACTOR = 10n ** BigInt(exports.MONEY_SCALE);
const MAX_UNITS = 10n ** BigInt(exports.MONEY_PRECISION) - 1n;
const MAX_INPUT_DIGITS = 10_000;
const NEGATIVE_ZERO = `-0.${'0'.repeat(exports.MONEY_SCALE)}`;
const DECIMAL_PATTERN = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;
const CANONICAL_PATTERN = new RegExp(`^-?(?:0|[1-9]\\d{0,${String(MAX_INTEGER_DIGITS - 1)}})\\.\\d{${String(exports.MONEY_SCALE)}}$`);
class MoneyFormatError extends Error {
    constructor(value) {
        super(`Not a decimal money value: ${JSON.stringify(value)}`);
        this.name = 'MoneyFormatError';
    }
}
exports.MoneyFormatError = MoneyFormatError;
class MoneyPrecisionError extends Error {
    constructor(value) {
        super(`Money value exceeds numeric(${String(exports.MONEY_PRECISION)},${String(exports.MONEY_SCALE)}): ${JSON.stringify(value)}`);
        this.name = 'MoneyPrecisionError';
    }
}
exports.MoneyPrecisionError = MoneyPrecisionError;
function parseDecimal(value) {
    const match = DECIMAL_PATTERN.exec(value.trim());
    if (match === null)
        throw new MoneyFormatError(value);
    const [, sign = '', intDigits = '', fracDigits = '', exponent = '0'] = match;
    const digitCount = intDigits.length + fracDigits.length;
    if (digitCount === 0 || digitCount > MAX_INPUT_DIGITS)
        throw new MoneyFormatError(value);
    const magnitude = BigInt(`${intDigits}${fracDigits}`);
    return {
        units: sign === '-' ? -magnitude : magnitude,
        exponent: Number(exponent) - fracDigits.length,
    };
}
// Half away from zero: a value exactly on the halfway point rounds up in magnitude, so it rounds up
// above zero and down below zero.
function scaledUnitsAt({ units, exponent }, scale, source) {
    if (units === 0n)
        return 0n;
    const magnitude = units < 0n ? -units : units;
    // Where the most significant digit sits relative to the decimal point. Both extremes resolve from
    // this, before any exponentiation, so an absurd exponent never builds a huge BigInt.
    const leadingPosition = magnitude.toString().length + exponent;
    if (leadingPosition > MAX_INTEGER_DIGITS)
        throw new MoneyPrecisionError(source);
    if (leadingPosition < -scale)
        return 0n;
    const shift = exponent + scale;
    if (shift >= 0)
        return units * 10n ** BigInt(shift);
    const divisor = 10n ** BigInt(-shift);
    const quotient = units / divisor;
    const remainder = units % divisor;
    const doubled = remainder < 0n ? -remainder * 2n : remainder * 2n;
    if (doubled < divisor)
        return quotient;
    return units < 0n ? quotient - 1n : quotient + 1n;
}
function toScaledUnits(value, source) {
    return scaledUnitsAt(value, exports.MONEY_SCALE, source);
}
function assertPrecision(units, source) {
    if ((units < 0n ? -units : units) > MAX_UNITS)
        throw new MoneyPrecisionError(source);
}
function format(units) {
    const magnitude = units < 0n ? -units : units;
    const whole = (magnitude / SCALE_FACTOR).toString();
    const fraction = (magnitude % SCALE_FACTOR).toString().padStart(exports.MONEY_SCALE, '0');
    return `${units < 0n ? '-' : ''}${whole}.${fraction}`;
}
function unitsOf(value) {
    return toScaledUnits(parseDecimal(value), value);
}
function fromUnits(units, source) {
    assertPrecision(units, source);
    return format(units);
}
// The single rounding boundary. Every money value in the V2 module enters through this function.
function toMoney(value) {
    return fromUnits(unitsOf(value), value);
}
// The print boundary. A figure an issued document states rounds once, half away from zero, to the
// print scale of 2, because the paper prints two decimals and the ledger must carry what the paper
// states. The value rounds directly from its full precision to the print scale - never through the
// money scale first, which would round twice (0.12495 is 0.12 at the print scale, but its
// money-scale form 0.1250 would round up to 0.13).
function toPrintMoney(value) {
    const printUnits = scaledUnitsAt(parseDecimal(value), exports.PRINT_SCALE, value);
    return fromUnits(printUnits * 10n ** BigInt(exports.MONEY_SCALE - exports.PRINT_SCALE), value);
}
// Accepts only what `format` emits: no leading zero, no sign on zero, exactly MONEY_SCALE fraction
// digits, and an integer part inside the stated precision. A total check, so it never throws.
function isMoney(value) {
    return CANONICAL_PATTERN.test(value) && value !== NEGATIVE_ZERO;
}
exports.ZERO_MONEY = toMoney('0');
function addMoney(left, right) {
    return fromUnits(unitsOf(left) + unitsOf(right), `${left} + ${right}`);
}
function subtractMoney(left, right) {
    return fromUnits(unitsOf(left) - unitsOf(right), `${left} - ${right}`);
}
// Sums the units in one pass, so the precision limit applies to the total and never to a running
// subtotal. A fold over addMoney would make the result depend on the order of the list.
function sumMoney(values) {
    let units = 0n;
    for (const value of values) {
        units += unitsOf(value);
    }
    return fromUnits(units, `sum of ${String(values.length)} values`);
}
function negateMoney(value) {
    return format(-unitsOf(value));
}
function absMoney(value) {
    const units = unitsOf(value);
    return format(units < 0n ? -units : units);
}
function compareMoney(left, right) {
    const difference = unitsOf(left) - unitsOf(right);
    if (difference < 0n)
        return -1;
    return difference > 0n ? 1 : 0;
}
function isZeroMoney(value) {
    return unitsOf(value) === 0n;
}
function isNegativeMoney(value) {
    return unitsOf(value) < 0n;
}
class MoneyAllocationError extends Error {
    constructor(reason) {
        super(`The amount cannot be allocated: ${reason}.`);
        this.name = 'MoneyAllocationError';
    }
}
exports.MoneyAllocationError = MoneyAllocationError;
// Floors totalUnits * weight / sumWeights per index, then hands the leftover minor units to the
// largest remainders in descending order, ties broken by ascending index (the standard largest-
// remainder / Hamilton method). Every weight and totalUnits is non-negative by the time this runs,
// so BigInt division already floors and never needs a separate floor step.
function largestRemainderShares(totalUnits, weights, sumWeights) {
    const floors = weights.map((weight) => (totalUnits * weight) / sumWeights);
    const remainders = weights.map((weight, index) => ({
        index,
        remainder: (totalUnits * weight) % sumWeights,
    }));
    remainders.sort((left, right) => {
        if (left.remainder === right.remainder)
            return left.index - right.index;
        return left.remainder > right.remainder ? -1 : 1;
    });
    const distributedUnits = floors.reduce((sum, share) => sum + share, 0n);
    let leftover = totalUnits - distributedUnits;
    const bumped = new Set();
    for (const { index } of remainders) {
        if (leftover <= 0n)
            break;
        bumped.add(index);
        leftover -= 1n;
    }
    return floors.map((share, index) => (bumped.has(index) ? share + 1n : share));
}
// The shared engine behind both public allocators. The parts always sum to total exactly, because
// every minor unit of totalUnits is placed by either the floor pass or the leftover pass.
function weightedSplit(total, weights, sumWeights) {
    const totalUnits = unitsOf(total);
    if (totalUnits < 0n)
        throw new MoneyAllocationError('the total is negative');
    const shares = largestRemainderShares(totalUnits, weights, sumWeights);
    return shares.map((share) => fromUnits(share, `allocation of ${total}`));
}
function allocateEqually(total, parts) {
    if (!Number.isInteger(parts) || parts < 1) {
        throw new MoneyAllocationError('the number of parts must be a positive integer');
    }
    const weights = Array.from({ length: parts }, () => 1n);
    return weightedSplit(total, weights, BigInt(parts));
}
// Each percentage is read at money scale (four decimal places) through the file's own unitsOf, so
// a percentage carrying more precision is rounded once, the same way a Decimal reaches toMoney.
function allocateByPercentage(total, percentages) {
    if (percentages.length === 0)
        throw new MoneyAllocationError('the percentage list is empty');
    const weights = percentages.map((percentage) => unitsOf(percentage));
    for (const weight of weights) {
        if (weight < 0n)
            throw new MoneyAllocationError('a percentage is negative');
    }
    const sumWeights = weights.reduce((sum, weight) => sum + weight, 0n);
    if (sumWeights !== unitsOf('100')) {
        throw new MoneyAllocationError('the percentages do not sum to exactly 100');
    }
    return weightedSplit(total, weights, sumWeights);
}
// Splits `total` proportionally to non-negative exact-decimal weights, with the same largest-
// remainder method allocateEqually and allocateByPercentage share, so the parts always sum to total
// exactly. Each weight is read through parseDecimal, the module's other entry into exact
// arithmetic, at its own full precision, so the split reflects the weights' true ratios and never a
// once-rounded approximation of them.
function allocateByWeight(total, weights) {
    if (weights.length === 0)
        throw new MoneyAllocationError('the weight list is empty');
    const parsed = weights.map((weight) => parseDecimal(weight));
    for (const { units } of parsed) {
        if (units < 0n)
            throw new MoneyAllocationError('a weight is negative');
    }
    const minExponent = Math.min(...parsed.map((entry) => entry.exponent));
    const scaledWeights = parsed.map(({ units, exponent }) => {
        const shift = exponent - minExponent;
        if (shift > MAX_INPUT_DIGITS) {
            throw new MoneyAllocationError('a weight carries excessive precision');
        }
        return units * 10n ** BigInt(shift);
    });
    const sumWeights = scaledWeights.reduce((sum, weight) => sum + weight, 0n);
    if (sumWeights === 0n)
        throw new MoneyAllocationError('the weights sum to zero');
    return weightedSplit(total, scaledWeights, sumWeights);
}
//# sourceMappingURL=money.js.map